import { getLogger } from '@packages/core/logging';

import { StsBackendOperation } from '../../../sts-backend-operation';

const logger = getLogger('techline.members');

/**
 * Parameters for adding a team member to a techline case. Creates a retailer profile
 * association that gives the specified user access to the case.
 */
export type AddTeamMemberParams = {
    /** The case number to add the member to. */
    caseNumber: string;

    /** The retailer number associated with the case. */
    retailerNumber: string;

    /** The username of the member to add. */
    username: string;
};

/**
 * Add a team member to a techline case by patching the retailer profile. This creates a new
 * retailer profile association for the specified user, giving them access to the case.
 */
export class AddTeamMember extends StsBackendOperation {
    /**
     * Patch the retailer profile to add a team member to the case.
     *
     * @param params - the case and member details
     * @returns true if the member was successfully added
     */
    async exec(params: AddTeamMemberParams): Promise<boolean> {
        const { caseNumber, retailerNumber, username } = params;
        try {
            const response = await this.client.patchRetailerProfile({
                casenumber: caseNumber,
                retailernumber: retailerNumber,
                statuscode: 2,
                username,
            });

            return Boolean(response?.success);
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to update retailer profile';
            logger.error('Failed to add the team member to the case', { error: e });
            throw new Error(message);
        }
    }
}
